import { useMemo, useState } from 'react';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { ApiService, DashboardSmallUser, ExportAuditLogsRequest } from 'api';
import { AUDIT_LOGS_TEST_IDS } from 'pages/AuditLogs/AuditLogs.testIds';
import { useAuditLogExport } from 'pages/AuditLogs/hooks';
import AuditLogExportDateRange from './AuditLogExportDateRange';
import { getMonthOptions, getUserLabel, getWeekOptions, shortcutMatchesRange } from './AuditLogExportDialog.utils';
import AuditLogExportShortcuts from './AuditLogExportShortcuts';

const AuditLogExportDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const now = useMemo(() => new Date(), []);
  const monthOptions = useMemo(() => getMonthOptions(now), [now]);
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].value);
  const [selectedWeek, setSelectedWeek] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(monthOptions[0].start);
  const [endDate, setEndDate] = useState<Date | null>(monthOptions[0].end);
  const [selectedUser, setSelectedUser] = useState<DashboardSmallUser | null>(null);
  const exportMutation = useAuditLogExport();
  const selectedMonthOption = monthOptions.find(option => option.value === selectedMonth) ?? monthOptions[0];
  const weekOptions = useMemo(() => getWeekOptions(selectedMonthOption), [selectedMonthOption]);
  const selectedWeekOption = weekOptions.find(option => option.value === selectedWeek);
  const isCustomRange = !shortcutMatchesRange(selectedWeekOption ?? selectedMonthOption, startDate, endDate);

  const { data: users = [], isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users-list', 'staff'],
    queryFn: () => ApiService.apiUsersStaffList(),
    enabled: open,
    staleTime: 60 * 60 * 1000,
  });

  const hasInvalidRange = !!startDate && !!endDate && startDate > endDate;

  const handleMonthChange = (value: string) => {
    const month = monthOptions.find(option => option.value === value);
    if (!month) {
      return;
    }
    setSelectedMonth(value);
    setSelectedWeek('');
    setStartDate(new Date(month.start));
    setEndDate(new Date(month.end));
  };

  const handleWeekChange = (value: string) => {
    setSelectedWeek(value);
    if (!value) {
      setStartDate(new Date(selectedMonthOption.start));
      setEndDate(new Date(selectedMonthOption.end));
      return;
    }
    const week = weekOptions.find(option => option.value === value);
    if (week) {
      setStartDate(new Date(week.start));
      setEndDate(new Date(week.end));
    }
  };

  const handleExport = () => {
    if (!startDate || hasInvalidRange) {
      return;
    }
    const payload: ExportAuditLogsRequest = {
      start_date: startDate.toISOString(),
      ...(endDate ? { end_date: endDate.toISOString() } : {}),
      ...(selectedUser?.id ? { user: selectedUser.id } : {}),
      product: ExportAuditLogsRequest.product.WORKBOARD,
    };
    const filenameEndDate = endDate ?? now;
    exportMutation.mutate(
      {
        payload,
        filename: `audit-logs-${startDate.toISOString().slice(0, 10)}-to-${filenameEndDate.toISOString().slice(0, 10)}.xlsx`,
      },
      { onSuccess: onClose }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" data-testid={AUDIT_LOGS_TEST_IDS.exportDialog}>
      <DialogTitle>Export audit logs</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Choose a month or week as a shortcut, then adjust the exact date and time range if needed.
          </Typography>
          <AuditLogExportShortcuts
            isCustomRange={isCustomRange}
            monthOptions={monthOptions}
            onMonthChange={handleMonthChange}
            onWeekChange={handleWeekChange}
            selectedMonth={selectedMonth}
            selectedWeek={selectedWeek}
            weekOptions={weekOptions}
          />
          <AuditLogExportDateRange
            endDate={endDate}
            hasInvalidRange={hasInvalidRange}
            onEndDateChange={setEndDate}
            onStartDateChange={setStartDate}
            startDate={startDate}
          />
          <Autocomplete
            options={users}
            value={selectedUser}
            loading={isLoadingUsers}
            getOptionLabel={getUserLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, value) => setSelectedUser(value)}
            renderInput={params => <TextField {...params} label="User (optional)" />}
            data-testid={AUDIT_LOGS_TEST_IDS.exportUserSelect}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleExport}
          loading={exportMutation.isPending}
          disabled={!startDate || hasInvalidRange}
          data-testid={AUDIT_LOGS_TEST_IDS.exportSubmitButton}>
          Export XLSX
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuditLogExportDialog;
